import { useState, useEffect, useRef, memo } from 'react';
import DOMPurify from 'dompurify';
import Box from '@mui/material/Box';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Badge from '@mui/material/Badge';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import Zoom from '@mui/material/Zoom';
import { FileUploader } from 'react-drag-drop-files';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import SendIcon from '@mui/icons-material/Send';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import FileService from '@Helpers/URLHelpers/FileService';
import { useCursor } from '@Root/CursorContext';
import { useReduxActiveUser } from '@Hooks/reduxActiveUserHook/useReduxActiveUser';
import { useReduxFileStorage } from '@Hooks/reduxFileStorageHook/useReduxFileStorage';
import History from '@IssueConsoleRepresentationalDisplay/History';
import Comment from '@IssueConsoleRepresentationalDisplay/Comment';
import ConfirmClientCommentForm from '@IssueConsoleForms/ConfirmClientCommentForm';
import TicketUserInteractionsService from '@Helpers/URLHelpers/KaiziqResolve/views/TicketUserInteractionsService';
import TicketService from '@Helpers/URLHelpers/KaiziqResolve/TicketService';
import ErrorHandler from '@Root/ErrorHandler';
import './CommentsTabs.scss';

function CommentsTabs(props) {
    const { cursor, setCursor } = useCursor();
    const [viewLoader, setViewLoader] = useState(true);
    const [ticket, setTicket] = useState(props?.ticket);
    const [commentList, setCommentList] = useState();
    const [historyList, setHistoryList] = useState();
    const [commentsTab, setCommentsTab] = useState(0);
    const [createComment, setCreateComment] = useState({});
    const [snackBarOpen, setSnackBarOpen] = useState(false);
    const [snackBarMessage, setSnackBarMessage] = useState('');
    const [snackBarType, setSnackBarType] = useState('success');
    const [createCommentText, setCreateCommentText] = useState('');
    const [clientCommentList, setClientCommentList] = useState([]);
    const [, setCloudSync] = useState('done');
    const [, setInternetException] = useState(false);
    const [ticketUserInteractionList, setTicketUserInteractionList] = useState([]);
    const [showConfirmClientCommentForm, setShowConfirmClientCommentForm] = useState(false);
    const { getTicketCommentsAndUploadFiles, uploadFileInS3 } = useReduxFileStorage();
    const {activeUserId, activeUser} = useReduxActiveUser();
    const ticketUserInteractionsService = new TicketUserInteractionsService();
    const ticketService = new TicketService();
    const errorHandler = new ErrorHandler();
    const fileService = new FileService();
    const className = 'commentsTabs';
    const listRef = useRef(null);

    useEffect(() => {
        renderData();
    }, [props]);

    useEffect(() => {
        // Auto-scroll to bottom when new comments arrive
        if (listRef.current) {
            listRef.current.scrollTop = listRef.current.scrollHeight;
        }
    }, [commentList, clientCommentList, ticketUserInteractionList]);

    const renderData = async () => {
        let commentsObj = await getTicketCommentsAndUploadFiles(ticket?.id);
        if(!!commentsObj) {
            let clientComments = !!commentsObj?.clientComments ? commentsObj?.clientComments : [];
            let comments = !!commentsObj?.comments ? commentsObj?.comments : [];
            setCommentList(comments);
            setClientCommentList(clientComments.sort().reverse());
        } else {
            setCommentList(JSON.parse(ticket?.comments));
            let clientCommentListObj = ticket.clientComments != null ? JSON.parse(ticket.clientComments) : [];
            setClientCommentList(clientCommentListObj.sort().reverse());
        }
        setHistoryList(JSON.parse(ticket?.history));
        await renderTicketUserInteractionData();
        setViewLoader(false);
    };

    const renderTicketUserInteractionData = async () => {
        let loadLoops = 0,
            loadingError = false,
            errorException;
        let ticketObj;
        for (loadLoops = 0; loadLoops < 10; loadLoops++) {
            try {
                ticketObj = await ticketUserInteractionsService.getListByItemId(ticket?.id);
                ticketObj = JSON.parse(ticketObj.body);
                setTicketUserInteractionList(ticketObj);
                loadingError = false;
                break;
            } catch (err) {
                errorHandler.logError('Ticket User Interaction Data', err);
                loadingError = true;
                errorException = err;
            }
        }
        if (loadLoops === 10 && loadingError === true) {
            errorHandler.handleError(errorException);
            setInternetException(true);
            return false;
        }
        return true;
    };

    const handleCommentChange = (e) => {
        let sanitizedComment = DOMPurify.sanitize(e.target.value);
        let createCommentMap = {
            text: sanitizedComment,
        };
        setCreateCommentText(sanitizedComment);
        setCreateComment(createCommentMap);
    };

    const handleSubmitDiscussionComment = async () => {
        if (!createCommentText?.trim()) return;
        try {
            setCursor('wait');
            setViewLoader(true);
            let createCommentMap = createComment;
            createCommentMap.id = commentList?.length + 1 || 1;
            createCommentMap.userId = activeUserId;
            createCommentMap.userType = 'admin';
            createCommentMap.email = activeUser.email;
            createCommentMap.timeStamp = new Date().valueOf();
            createCommentMap.pinnedComment = false;
            let body = {
                id: ticket.id,
                comment: createCommentMap,
            };
            let ticketObj = await ticketService.addDiscussionComment(body);
            if (ticketObj.status < 300) {
                ticketObj = JSON.parse(ticketObj?.body);
                setTicket(ticketObj);
                setCreateCommentText('');
                setCreateComment({});
                setCommentList(JSON.parse(ticketObj?.comments));
                let clientCommentListObj = ticketObj.clientComments != null ? JSON.parse(ticketObj.clientComments) : [];
                setClientCommentList(clientCommentListObj.sort().reverse());
                setHistoryList(JSON.parse(ticketObj?.history));
                await renderTicketUserInteractionData();
                setSnackBarMessage('Comment added successfully!');
                setSnackBarType('success');
                setSnackBarOpen(true);
            } else {
                setSnackBarMessage('Failed to add comment');
                setSnackBarType('error');
                setSnackBarOpen(true);
            }
        } catch (err) {
            errorHandler.handleError(err);
            setSnackBarMessage('Failed to add comment');
            setSnackBarType('error');
            setSnackBarOpen(true);
        }
        setCursor('auto');
        setViewLoader(false);
    };

    const handleSubmitClientComments = async () => {
        if (!createCommentText?.trim()) return;
        try {
            setCursor('wait');
            setViewLoader(true);
            let createCommentMap = createComment;
            createCommentMap.id = clientCommentList.length + 1;
            createCommentMap.userId = activeUserId;
            createCommentMap.userType = 'admin';
            createCommentMap.email = activeUser.email;
            createCommentMap.timeStamp = new Date().valueOf();
            createCommentMap.pinnedComment = false;
            let body = {
                id: ticket.id,
                comment: createCommentMap,
                source: 'app',
            };
            let ticketObj = await ticketService.addClientComment(body);
            if (ticketObj.status < 300) {
                ticketObj = JSON.parse(ticketObj?.body);
                setTicket(ticketObj);
                setCommentList(JSON.parse(ticketObj?.comments));
                let clientCommentListObj = ticketObj.clientComments != null ? JSON.parse(ticketObj.clientComments) : [];
                setClientCommentList(clientCommentListObj.sort().reverse());
                setHistoryList(JSON.parse(ticketObj?.history));
                setCreateCommentText('');
                setCreateComment({});
                setSnackBarMessage('Comment sent to client!');
                setSnackBarType('success');
                setSnackBarOpen(true);
                setShowConfirmClientCommentForm(false);
            } else {
                setSnackBarMessage('Failed to send comment');
                setSnackBarType('error');
                setSnackBarOpen(true);
            }
        } catch (err) {
            errorHandler.handleError(err);
            setSnackBarMessage('Failed to send comment');
            setSnackBarType('error');
            setSnackBarOpen(true);
        }
        setCursor('auto');
        setViewLoader(false);
    };

    const toggleShowConfirmClientCommentForm = () => {
        if (!createCommentText?.trim()) return;
        setShowConfirmClientCommentForm(!showConfirmClientCommentForm);
    };

    const onCommentKeyPress = (e) => {
        if (e?.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            if (commentsTab === 0) {
                handleSubmitDiscussionComment();
            } else {
                toggleShowConfirmClientCommentForm();
            }
        }
    };

    const handleSubmitComment = () => {
        if (commentsTab === 0) {
            handleSubmitDiscussionComment();
        } else {
            toggleShowConfirmClientCommentForm();
        }
    };

    const handleSubmitCommentFile = async (file) => {
        setViewLoader(true);
        setCursor('wait');
        if (!!file) {
            let fileSize = file.size / (1024 * 1024);
            if (fileSize <= 2) {
                const url = await convertFileToBase64(file);
                let newFileObj = {
                    file: url,
                    fileName: file.name,
                    fileSize: fileSize,
                    fileType: file.type,
                    projectId: ticket.projectId,
                };
                let body = {
                    fileName: file.name,
                    tableType: 'TICKET',
                    itemId: ticket.id,
                    file: newFileObj,
                    projectId: ticket.projectId,
                    userId: activeUserId,
                    storeType: 'comment',
                };
                newFileObj = await uploadFileInS3(body);
                try {
                    setCloudSync('syncing');
                    let createCommentMap = {};
                    createCommentMap.userId = activeUserId;
                    createCommentMap.type = 'File';
                    createCommentMap.userType = 'Admin';
                    createCommentMap.text = file.name;
                    createCommentMap.fileId = newFileObj.id;
                    createCommentMap.timeStamp = new Date().valueOf();
                    let body = {
                        id: ticket.id,
                        comment: createCommentMap,
                    };
                    if (commentsTab === 0) {
                        let ticketObj = await ticketService.addDiscussionComment(body);
                        if (ticketObj.status < 300) {
                            ticketObj = JSON.parse(ticketObj?.body);
                            setTicket(ticketObj);
                            setCreateCommentText('');
                            setCreateComment({});
                            setCommentList(JSON.parse(ticketObj?.comments));
                            let clientCommentListObj = ticketObj.clientComments != null ? JSON.parse(ticketObj.clientComments) : [];
                            setClientCommentList(clientCommentListObj.sort().reverse());
                            setHistoryList(JSON.parse(ticketObj?.history));
                            await renderTicketUserInteractionData();
                            setSnackBarMessage('File attached successfully!');
                            setSnackBarType('success');
                            setSnackBarOpen(true);
                        } else {
                            setSnackBarMessage('Failed to attach file');
                            setSnackBarType('error');
                            setSnackBarOpen(true);
                        }
                    } else {
                        body.source = 'app';
                        let ticketObj = await ticketService.addClientComment(body);
                        if (ticketObj.status < 300) {
                            ticketObj = JSON.parse(ticketObj?.body);
                            setTicket(ticketObj);
                            setCommentList(JSON.parse(ticketObj?.comments));
                            let clientCommentListObj = ticketObj.clientComments != null ? JSON.parse(ticketObj.clientComments) : [];
                            setClientCommentList(clientCommentListObj.sort().reverse());
                            setHistoryList(JSON.parse(ticketObj?.history));
                            setCreateCommentText('');
                            setCreateComment({});
                            setSnackBarMessage('File sent to client!');
                            setSnackBarType('success');
                            setSnackBarOpen(true);
                            setShowConfirmClientCommentForm(false);
                        } else {
                            setSnackBarMessage('Failed to send file');
                            setSnackBarType('error');
                            setSnackBarOpen(true);
                        }
                    }
                    setCloudSync('done');
                } catch (err) {
                    errorHandler.logError('File Upload to Comments', err);
                    setCloudSync('failed');
                    setSnackBarMessage('File upload failed!');
                    setSnackBarType('error');
                    setSnackBarOpen(true);
                }
            } else {
                setCloudSync('failed');
                setSnackBarMessage('File size must be under 2MB');
                setSnackBarType('error');
                setSnackBarOpen(true);
            }
        }
        setCursor('auto');
        setViewLoader(false);
    };

    const convertFileToBase64 = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result);
            reader.onerror = (error) => reject(error);
        });
    };

    const handleCommentsTab = (e, value) => {
        setCommentsTab(value);
    };

    const handleSnackBarClose = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setSnackBarOpen(false);
    };

    const discussionCount = ticketUserInteractionList?.length || 0;
    const clientCommentsCount = clientCommentList?.length || 0;

    return (
        <Box className={className + ' height-wfa width-wfa'}>
            {/* Header with Tabs */}
            <Box className={className + '__header'}>
                <Tabs
                    value={commentsTab}
                    onChange={handleCommentsTab}
                    variant="fullWidth"
                    className={className + '__tabs'}
                    TabIndicatorProps={{
                        style: { height: 3, borderRadius: '3px 3px 0 0' }
                    }}
                >
                    <Tab
                        icon={<ForumOutlinedIcon />}
                        iconPosition="start"
                        label={
                            <Badge badgeContent={discussionCount} color="primary" max={99}>
                                <span style={{ marginRight: discussionCount > 0 ? 20 : 0 }}>Discussions</span>
                            </Badge>
                        }
                        value={0}
                        className={className + '__tab'}
                    />
                    <Tab
                        icon={<ChatBubbleOutlineIcon />}
                        iconPosition="start"
                        label={
                            <Badge badgeContent={clientCommentsCount} color="secondary" max={99}>
                                <span style={{ marginRight: clientCommentsCount > 0 ? 20 : 0 }}>Comments</span>
                            </Badge>
                        }
                        value={1}
                        className={className + '__tab'}
                    />
                </Tabs>
            </Box>

            <Divider />

            {/* Body - Comments List */}
            <Box className={className + '__body'}>
                {viewLoader ? (
                    <Box className={className + '__loader'}>
                        <LinearProgress />
                        <Typography className={className + '__loaderText'}>Loading...</Typography>
                    </Box>
                ) : (
                    <Box className={className + '__list'} ref={listRef}>
                        {commentsTab === 0 ? (
                            ticketUserInteractionList?.length > 0 ? (
                                ticketUserInteractionList.map((userInteraction, idx) => (
                                    <History
                                        key={idx}
                                        ticket={props?.ticket}
                                        userInteraction={userInteraction}
                                        adminFieldList={props?.adminFieldList}
                                        historyList={historyList}
                                        priorityList={props?.priorityList}
                                        commentList={commentList}
                                    />
                                ))
                            ) : (
                                <Box className={className + '__empty'}>
                                    <ForumOutlinedIcon className={className + '__emptyIcon'} />
                                    <Typography className={className + '__emptyText'}>No discussions yet</Typography>
                                    <Typography className={className + '__emptySubtext'}>
                                        Start a discussion with your team
                                    </Typography>
                                </Box>
                            )
                        ) : clientCommentList?.length > 0 ? (
                            clientCommentList.map((commentItem, idx) => (
                                <Comment
                                    key={idx}
                                    ticket={props?.ticket}
                                    reloadData={() => {}}
                                    userInteraction={{ linkId: commentItem.id }}
                                    commentList={clientCommentList}
                                />
                            ))
                        ) : (
                            <Box className={className + '__empty'}>
                                <ChatBubbleOutlineIcon className={className + '__emptyIcon'} />
                                <Typography className={className + '__emptyText'}>No comments yet</Typography>
                                <Typography className={className + '__emptySubtext'}>
                                    Send a comment to your client
                                </Typography>
                            </Box>
                        )}
                    </Box>
                )}
            </Box>

            <Divider />

            {/* Footer - Input Box */}
            <Box className={className + '__footer'}>
                <Box className={className + '__inputRow'}>
                    <TextField
                        placeholder={commentsTab === 0 ? 'Type a message...' : 'Message to client...'}
                        className={className + '__input'}
                        multiline
                        value={createCommentText}
                        maxRows={4}
                        minRows={1}
                        disabled={cursor === 'wait' || viewLoader}
                        onKeyDown={onCommentKeyPress}
                        inputProps={{ maxLength: 500 }}
                        onChange={handleCommentChange}
                        variant="outlined"
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <FileUploader handleChange={handleSubmitCommentFile} disabled={cursor === 'wait'}>
                                        <Tooltip title="Attach file (max 2MB)" TransitionComponent={Zoom} arrow>
                                            <IconButton size="small" className={className + '__attachBtn'}>
                                                <AttachFileIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </FileUploader>
                                </InputAdornment>
                            ),
                        }}
                    />
                    {/* WhatsApp keeps send as its own circular button beside the input pill,
                        not an inline icon inside it. */}
                    <Tooltip
                        title={commentsTab === 0 ? 'Send message' : 'Send to client'}
                        TransitionComponent={Zoom}
                        arrow
                    >
                        <span>
                            <IconButton
                                onClick={handleSubmitComment}
                                disabled={cursor === 'wait' || !createCommentText?.trim()}
                                className={className + '__sendBtn'}
                            >
                                <SendIcon />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Box>
                <Typography className={className + '__hint'}>
                    {createCommentText?.length || 0} / 500 • Press Enter to send
                </Typography>
            </Box>

            {/* Confirm Dialog */}
            {showConfirmClientCommentForm && (
                <ConfirmClientCommentForm
                    open={showConfirmClientCommentForm}
                    closePopup={toggleShowConfirmClientCommentForm}
                    handleSubmitClientComments={handleSubmitClientComments}
                    commentText={createCommentText}
                />
            )}

            {/* Snackbar */}
            <Snackbar
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
                open={snackBarOpen}
                autoHideDuration={5000}
                onClose={handleSnackBarClose}
            >
                <Alert onClose={handleSnackBarClose} severity={snackBarType} variant="filled" sx={{ width: '100%' }}>
                    {snackBarMessage}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default memo(CommentsTabs);